import toast from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

interface QuizCopyParams {
  quizId: string;
  title: string;
  isLocked?: boolean;
}

/**
 * useQuizCopy - 퀴즈 복사 mutation hook
 *
 * @description
 * 퀴즈 복사 기능을 제공하는 hook입니다.
 * 현재는 /duplicate/[id] 페이지로 라우팅하는 방식으로 구현되어 있습니다.
 * 백엔드에 POST /quizsets/{id}/copy API가 추가되면
 * 이 hook의 mutationFn만 수정하여 API 호출 방식으로 전환할 수 있습니다.
 *
 * @example
 * ```tsx
 * const quizCopy = useQuizCopy();
 *
 * <Button
 *   onClick={() => quizCopy.mutate({ quizId: '123', title: 'My Quiz' })}
 *   disabled={quizCopy.isPending}>
 *   {quizCopy.isPending ? '복사 중...' : '복사하기'}
 * </Button>
 * ```
 */
export const useQuizCopy = () => {
  const router = useRouter();
  const { t } = useTranslation(['common']);

  return useMutation({
    mutationFn: async ({ quizId, isLocked }: QuizCopyParams) => {
      // 현재는 /duplicate/[id] 페이지로 라우팅
      // 백엔드 API가 추가되면 여기서 API 호출로 변경
      await router.push({
        pathname: `/duplicate/${quizId}`,
      });

      return { quizId, isLocked };
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onSuccess: (_data, variables) => {
      // 페이지 전환되므로 별도 토스트 불필요
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    onError: (error: Error, variables) => {
      // 라우팅 실패 시 에러 토스트
      toast.error(t('common:quizDetail.copy.failed'), {
        duration: 4000,
      });

      console.error('Quiz copy failed:', error);
    },
  });
};
